import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Content } from '@/components/cardWithThreeFooterBox';
import { GridWidget } from '@/components/GridWidget.tsx';
import { fromApiDateTime, Icon } from '@/utils';

import {
  ChartInterval,
  ChartProps,
  ChartType,
  ICardDataState,
  PlotType,
  ScatterPlotChartConfigType,
} from '../types.ts';
import { ScatterPlotHighChart } from './scatterPlotCard/ScatterPlotHighChart.tsx';

/**
 * Set config or use localStorage via useChartStorage
 * @param id
 * @param onOpenModal
 * @param onRemove
 * @param config
 * @param hideEditChart
 * @constructor
 */
export const ScatterPlotCard = ({
  id,
  onOpenModal,
  config,
  hideEditChart,
  ...rest
}: ChartProps<ScatterPlotChartConfigType>) => {
  const { t } = useTranslation(['common', 'chartConfig']);

  const [chartDataStat, setChartDataStat] = useState<ICardDataState>({
    isLoading: true,
    isError: false,
    isEmpty: false,
  });

  const [chartName, setChartName] = useState<string | undefined>(undefined);

  const chartConfig = useMemo<ScatterPlotChartConfigType>(() => {
    const cfg = {
      ...defaultConfig,
      ...config,
    };

    // Ensure datetimes are local DateTimes (API strings may include an offset).
    if (typeof cfg.start === 'string') cfg.start = fromApiDateTime(cfg.start);
    if (typeof cfg.end === 'string') cfg.end = fromApiDateTime(cfg.end);

    return cfg;
  }, [config]);

  return (
    <GridWidget
      {...rest}
      id={id}
      headerTitle={chartConfig?.title || t('chartConfig:scatterPlot.title')}
      headerSubtitle={chartName}
      {...chartDataStat}
      emptyMessages={[t('chartConfig:noReadings'), t('chartConfig:noReadingsMessage')]}
      toolbarActionList={
        hideEditChart
          ? undefined
          : [
              {
                id: 'edit',
                label: t('common:edit'),
                onClick: onOpenModal,
                Icon: Icon.SlidersHorizontal,
              },
            ]
      }
    >
      <Content>
        <ScatterPlotHighChart
          config={chartConfig}
          setChartDataStat={setChartDataStat}
          onChartNameChange={setChartName}
        />
      </Content>
    </GridWidget>
  );
};

// ************************************
// Constants
// ************************************
const defaultConfig: ScatterPlotChartConfigType = {
  plotType: PlotType.SCATTER,
  title: '',
  sensorIdA: '',
  sensorIdB: '',
  providerA: '',
  providerB: '',
  type: ChartType.HISTORICAL,
  interval: ChartInterval.Day,
};
